<template>
  <div id="workflowsInstance">
    <div class="row">
      <div class="col-lg-6 col-xl-4">
        <card id="instanceResultsProperties" no-header no-body no-footer>
          <table slot="card-table" class="table table-striped table-sm table-ellipsis mb-0">
            <tbody>
              <tr>
                <td class="text-uppercase">{{ $t('w-instance:equipment') }}</td>
                <td><router-link id="instance-equipmentId" :to="getEquipmentRoute(instance).routeObject">{{ getEquipmentRoute(instance).equipmentId }}</router-link></td>
              </tr>
              <tr>
                <td class="text-uppercase">{{ $t('w-instance:workflow') }}</td>
                <td id="instance-templateName" :title="instance.templateName">{{ instance.templateName }}</td>
              </tr>
              <tr>
                <td class="text-uppercase">{{ $t('w-instance:status') }}</td>
                <td id="instance-status" :class="getStatusColor(instance.status)">{{ instance.status }}</td>
              </tr>
              <tr>
                <td class="text-uppercase">{{ $t('w-instance:startTime') }}</td>
                <td id="instance-startTime">{{ instance.startTime | date }}</td>
              </tr>
              <tr>
                <td class="text-uppercase">{{ $t('w-instance:endTime') }}</td>
                <td id="instance-endTime">{{ instance.endTime | date }}</td>
              </tr>
              <tr>
                <td class="text-uppercase">{{ $t('w-instance:duration') }}</td>
                <td id="instance-durationInMillis">{{ instance.durationInMillis | duration }}</td>
              </tr>
            </tbody>
          </table>
        </card>

        <div v-if="!instance.endTime && instanceUpdateTime" class="text-muted font-italic">{{ $t('w-instance:refreshing') | capitalize }} <countdown :end-timestamp="instanceUpdateTime" /></div>
      </div>

      <div v-if="instance.endTime" class="col-lg-12 col-xl-8">
        <card id="instanceResults" :title="$t('w-instance:results')" no-body no-footer>
          <table slot="card-table" class="table table-bordered mb-0">
            <thead>
              <tr>
                <th>{{ $t('w-instance:task') | capitalize }}</th>
                <th>{{ $t('w-instance:status') | capitalize }}</th>
                <th>{{ $t('w-instance:result') | capitalize }}</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="(result, indexResult) in formattedResults" :key="indexResult">
                <td>
                  <template v-if="!isRole.admin || !result.taskId">{{ result.webservice }}</template>
                  <router-link v-else
                               :to="{
                                 name: 'gatewayTasks',
                                 params: { eui: getEquipmentRoute(instance).equipmentId },
                                 query: { taskId: result.taskId }
                               }"
                  >
                    {{ result.webservice }}
                  </router-link>
                </td>
                <td :class="getStatusColor(result.status)">{{ result.status }}</td>
                <td><a href @click.prevent="openModalResultDetails(result.result)">{{ result.result | stringify | substring(50) }}</a></td>
              </tr>
            </tbody>
          </table>
        </card>
      </div>
    </div>

    <modal id="modalResultDetails"
           v-model="showModalResultDetails"
           :title="$t('w-instance:resultDetails') | capitalize"
           size="lg"
    >
      <div slot="modal-body">
        <!-- Need to use v-html to insert formatted json (secured content) -->
        <!-- eslint-disable-next-line -->
        <pre v-html="bodyModalResultDetails" />
      </div>
    </modal>
  </div>
</template>

<script>
import { filter, each, sortBy, isObject, omit } from 'lodash-es'
import to from '@/modules/await-to-js'

export default {
  titleKey: 'workflowsInstance',

  props: {
    // id of the instance
    id: {
      type: String,
      required: true
    }
  },

  data () {
    return {
      // Instance data
      instance: {},
      instanceResults: [],
      // Polling timer
      instanceTimer: null,
      instanceUpdateTime: null,
      // Details modal
      showModalResultDetails: false,
      bodyModalResultDetails: ''
    }
  },

  computed: {
    // Get userTasks that executed the webservices
    // In case of synchronous webservice, result is in that task
    userTasksExecutor () {
      return filter(this.instanceResults, result => (result.activityType === 'userTask' && result.assignee === 'workflowStepExecutor'))
    },

    // Get userTasks that checked the webservices results
    // In case of asynchronous webservice, result is in that task
    userTasksCheck () {
      return filter(this.instanceResults, result => (result.activityType === 'userTask' && result.assignee === 'tasksStatusCheck'))
    },

    /* Format results to have an object:
     * <equipment>: [<task1Result>, <task2Result>, ...]
     *
     * Possible responses in serviceTasks:
     * - sync get:     response: { syncResult: <getCustomersDto> }
     * - sync create:  response: { syncResult: 200, result: <customerDto> }
     * - async get:    response: { result: 0, <eui1>: <commandDto>, <eui2>: <commandDto>, ... }
     */
    formattedResults () {
      const results = []
      const asyncTaskResult = JSON.stringify({ result: 0 })

      each(this.userTasksExecutor, (task, index) => {
        // By default, get response from userTasksExecutor
        let taskResponse = task.response ? JSON.parse(task.response) : {}
        let status = task.status

        // If response is result:0, check response from userTasksCheck (asynchronous task)
        if (task.response === asyncTaskResult) {
          const taskCheck = this.userTasksCheck[index]
          taskResponse = taskCheck && taskCheck.response ? JSON.parse(taskCheck.response) : taskResponse
          status = taskCheck.status
        }

        // Get the corresponding BSC taskId and task response
        const taskId = taskResponse.taskId
        taskResponse = taskResponse[this.getEquipmentRoute(this.instance).equipmentId]

        results.push({
          webservice: task.activityName,
          status: status,
          result: taskResponse,
          taskId
        })
      })

      return results
    }
  },

  watch: {
    id () {
      // Reset
      this.instance = {}
      this.instanceResults = []
      window.clearTimeout(this.instanceTimer)
      this.instanceUpdateTime = null
      this.showModalResultDetails = false
      this.bodyModalResultDetails = ''

      // Get instance
      this.getWorkflowInstance()
    }
  },

  created () {
    // Get instance on start
    this.getWorkflowInstance()
  },

  destroyed () {
    // Clear timer on destroy
    window.clearTimeout(this.instanceTimer)
  },

  methods: {
    // Get instance details
    async getWorkflowInstance () {
      let error, response
      [error, response] = await to(this.$api.getWorkflowInstance(this.$store.getters.application, this.id))
      if (error || !response) return

      this.instance = response.data

      // Get steps history
      this.getWorkflowInstanceActivities()

      // If instance is not ended, calculate duration and start polling to update page
      if (!this.instance.endTime) {
        this.instance.durationInMillis = Date.now() - this.instance.startTime

        this.startPolling()
      }
    },

    // Get steps history
    async getWorkflowInstanceActivities () {
      let error, response
      [error, response] = await to(this.$api.getWorkflowInstanceActivities(this.instance, this.id))
      if (error || !response) return

      // Get results if ended
      if (this.instance.endTime) {
        this.instanceResults = sortBy(response.data.list, ['startTime', 'endTime'])
      }
    },

    // Start polling to update results if instance not ended (every 30s)
    startPolling () {
      window.clearTimeout(this.instanceTimer)
      if (this._isDestroyed || this._isBeingDestroyed) return

      this.instanceUpdateTime = Date.now() + 30000
      this.instanceTimer = window.setTimeout(this.getWorkflowInstance, 30000)
    },

    // Display a modal with full result
    openModalResultDetails (result) {
      // On error, result will be a string
      if (isObject(result)) {
        // Remove links from result dto
        result = omit(result, ['links'])
      }
      // Display modal
      this.bodyModalResultDetails = isObject(result) ? this.$utils.highlight(result) : result
      this.showModalResultDetails = true
    },

    // KO: red, PENDING: orange, OK: green
    getStatusColor (status) {
      return status === 'KO' ? 'text-danger' : (status === 'OK' ? 'text-success' : 'text-warning')
    },

    // Create a route object with equipmentId
    // Equipment id: eui for bsc, devEui for lns
    getEquipmentRoute (instance) {
      const defaultReturn = {
        routeObject: {},
        equipmentId: ''
      }

      if (!instance.processVariables) {
        return defaultReturn
      } else if (instance.processVariables.euis) {
        return {
          routeObject: { name: 'gatewayRoute', params: { eui: instance.processVariables.euis } },
          equipmentId: instance.processVariables.euis
        }
      } else if (instance.processVariables.devEui) {
        return {
          routeObject: { name: 'endDevice', params: { devEui: instance.processVariables.devEui } },
          equipmentId: instance.processVariables.devEui
        }
      } else {
        return defaultReturn
      }
    }
  }
}
</script>
